/**
 * Zone-aware reverse geocoder + iauction path → canonical Spitogatos
 * slug normalizer. Replaces the old `coordsToOurSlug` and
 * `iauctionLocationsToOurSlug` once the zones table is populated.
 *
 * Two layers:
 *
 *   1. coordsToZoneSlug(lat, lng) — point-in-polygon resolution against
 *      bundled zone polygons (level 3 first, then level 2, then level 1).
 *
 *   2. iauctionPathToZoneSlug(locations[]) — canonicalize iauction's
 *      hierarchical path strings to a Spitogatos zone slug. Walks each
 *      path segment deepest-first, computes a stem-key, and looks up
 *      against the bundled stem-key index built from zone names.
 *
 * Both fall back to coordinate-based resolution where possible.
 *
 * The bundled zone data is generated from scripts/final-zones.json by
 * scripts/build-zones-bundle.mjs at build time.
 */

import type { ZonesBundle, ZoneEntry } from './zones-bundle.types';
import { ZONES_BUNDLE } from './zones-bundle';

/**
 * Polygon sources that are NOT real geography — synthetic shapes generated
 * to fill gaps in the catalog. We must NOT trust point-in-polygon results
 * against these:
 *
 *   - 'parent' (parent_inherited): a clone of the L2 parent's polygon. Any
 *     point in the parent matches all its parent_inherited L3 children, so
 *     PiP would attribute the point to whichever L3 child happens to be
 *     iterated first — effectively random.
 *   - 'voronoi': geometric tessellation around centroids. Cell boundaries
 *     are arbitrary lines between centroid pairs, not real geography.
 *   - 'nominatim_bbox': a rectangle around the centroid. Includes large
 *     areas the zone doesn't actually cover.
 *
 * Skipping these during resolution makes the resolver fall through to a
 * less-specific level (L3 → L2 → L1) where a REAL polygon contains the
 * point — yielding a coarser-but-correct attribution rather than a
 * specific-but-wrong one.
 */
const FAKE_POLYGON_SOURCES: ReadonlySet<string> = new Set([
  'parent',
  'voronoi',
  'nominatim_bbox',
]);

/**
 * Polygon sources that represent REAL geographic boundaries (not synthetic
 * gap-fillers). Authoritative list copied from apps/dashboard/src/pages/Zones.tsx.
 * Used by nearestRealL3 to restrict nearest-neighbour snapping to zones that
 * actually have real geography.
 */
export const REAL_POLY_SOURCES: ReadonlySet<string> = new Set([
  'cityofathens_geitonies',
  'cityofathens_synoikia',
  'cityofathens_dimotikes_koinotites',
  'spitogatos_concaveman',
  'spitogatos_small_hull',
  'listings_concaveman',
  'l3_envelope',
  'iauction_concaveman',
  'iauction_small_hull',
  'iauction_hull',
  'osm_admin_8',
  'osm_admin_9',
  'osm_legacy',
  'piraeus',
  'piraeus_dk',
  'nominatim_polygon',
]);

/**
 * Reverse-geocode (lat, lng) to the most specific zone slug whose polygon
 * contains the point. Walks zones in order: L3 → L2 → L1, returning the
 * first containing zone whose polygon source is real. (L3 is more specific
 * than L2, so we prefer it; but only when its polygon is genuine.)
 *
 * Returns null if the point is outside coverage.
 */
export function coordsToZoneSlug(lat: number, lng: number): string | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  // Sort: L3 first, then L2, then L1 (more specific wins). Fake-polygon
  // zones are skipped at every level so the resolver falls through to a
  // coarser real polygon rather than picking a synthetic match.
  for (const level of [3, 2, 1] as const) {
    for (const z of ZONES_BUNDLE.zones) {
      if (z.level !== level) continue;
      if (FAKE_POLYGON_SOURCES.has(z.polygon_source)) continue;
      if (!containsPoint(z, lat, lng)) continue;
      return z.slug;
    }
  }
  return null;
}

function containsPoint(z: ZoneEntry, lat: number, lng: number): boolean {
  if (!z.bbox || !z.rings) return false;
  // bbox: [minLng, minLat, maxLng, maxLat]
  if (lng < z.bbox[0] || lng > z.bbox[2]) return false;
  if (lat < z.bbox[1] || lat > z.bbox[3]) return false;
  for (const ring of z.rings) {
    if (pointInRing(lng, lat, ring)) return true;
  }
  return false;
}

function pointInRing(
  lng: number,
  lat: number,
  ring: ReadonlyArray<readonly [number, number]>,
): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const vi = ring[i];
    const vj = ring[j];
    if (!vi || !vj) continue;
    const [xi, yi] = vi;
    const [xj, yj] = vj;
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Strip Greek accents and lowercase. Used as a normalization base.
 */
function stripAccents(s: string): string {
  return (s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Crude Greek stemmer — strips common case-ending suffixes so
 * "αγιας" and "αγια" map to the same stem.
 */
function greekStem(word: string): string {
  if (!word || word.length < 4) return word;
  // Order matters: longest matching suffix wins. Greek case endings vary
  // by declension; the list below covers the productive ones we hit on
  // place names. `ιου` (genitive of -ι nouns: Λουτρακίου → λουτρακ) and
  // `ως` (older masculine genitive: Πειραιώς → πειραι) were added in
  // 2026-05 after detectZoneFromText regressions.
  const suffixes = [
    'εως', 'εων', 'ιων', 'ιου', 'ηρα', 'ητα',
    'ος', 'ου', 'ων', 'ης', 'ας', 'ες', 'οι', 'ως', 'εις', 'ις', 'εω',
    'α', 'η', 'ι', 'ο', 'υ', 'ς',
  ];
  for (const sfx of suffixes) {
    if (word.endsWith(sfx) && word.length - sfx.length >= 3) {
      return word.slice(0, -sfx.length);
    }
  }
  return word;
}

/**
 * Spitogatos's slugs use Latin transliteration with quirks (kipseli vs
 * kypseli, kalithea vs kallithea, etc.). For matching iauction's path
 * tokens against zone slugs we use a stem-key built by:
 *   - lowercasing
 *   - stripping diacritics
 *   - splitting on hyphens
 *   - greekStem-ing each token (works on transliterations too — any
 *     trailing 's' / 'a' / 'o' gets stripped consistently)
 *   - sorting + joining
 *
 * Both Spitogatos slug and iauction segment go through this, so
 * "agia-paraskevi-athina-voreia-proastia" and "agia-paraskevi" both
 * have stem-tokens including {agi, paraskev}.
 */
function stemKey(s: string): string {
  return [...new Set(
    stripAccents(s)
      .split(/[\s\-_]+/)
      .filter(Boolean)
      .map(greekStem)
  )].sort().join(' ');
}

/**
 * Build a one-time index: for each zone, compute the stem-token-set of
 * its slug. Multiple zones can share a stem-key (e.g., "agia-paraskevi"
 * matches "agia-paraskevi-athina-voreia-proastia"); store all matches
 * with their level so the resolver can prefer L2 over L3 etc.
 */
const ZONE_STEMS: ReadonlyMap<string, ReadonlyArray<{ slug: string; level: number }>> = (() => {
  const map = new Map<string, Array<{ slug: string; level: number }>>();
  for (const z of ZONES_BUNDLE.zones) {
    const tokens = new Set(
      stripAccents(z.slug).split(/[\s\-_]+/).filter(Boolean).map(greekStem),
    );
    // Index every non-trivial subset (size >= 1) as a candidate match key.
    // For practical lookup, we only index the FULL stem-key (sorted tokens
    // joined) — partial matches are handled by walking iauction path
    // segments deepest-first.
    const k = [...tokens].sort().join(' ');
    if (!k) continue;
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push({ slug: z.slug, level: z.level });
  }
  return map;
})();

/**
 * Normalize an iauction `locations[]` path to a canonical Spitogatos
 * zone slug. Walks deepest-first; for each path segment, computes its
 * stem-key against the zone-slug index. First match wins.
 *
 * Falls back to null if no segment matches any zone.
 */
export function iauctionPathToZoneSlug(locations: ReadonlyArray<string>): string | null {
  if (!locations || locations.length === 0) return null;
  // Try most-specific (deepest) first
  for (let i = locations.length - 1; i >= 0; i--) {
    const seg = locations[i];
    if (!seg) continue;
    // Try the segment itself, then trim leading region prefixes
    // (athens-center-X → strip athens, strip center, etc.)
    const parts = seg.split('-');
    for (let start = 0; start < parts.length; start++) {
      const candidate = parts.slice(start).join('-');
      if (!candidate) continue;
      const k = stemKey(candidate);
      const matches = ZONE_STEMS.get(k);
      if (matches && matches[0]) {
        // Prefer L2 over L1; L3 over both (more specific = closer match)
        const sorted = [...matches].sort((a, b) => b.level - a.level);
        return sorted[0]!.slug;
      }
    }
  }
  return null;
}

/**
 * Resolve to the most-specific zone slug for a listing, preferring
 * coordinate-based geocoding (always reliable when coords present).
 * Falls back to iauction path normalization for the rare case of a
 * listing missing coordinates.
 */
export function resolveListingZone(
  coords: readonly [number, number] | null | undefined,
  iauctionLocations: ReadonlyArray<string> | null | undefined,
): string | null {
  if (coords && Array.isArray(coords) && coords.length === 2) {
    const slug = coordsToZoneSlug(coords[0]!, coords[1]!);
    if (slug) return slug;
  }
  if (iauctionLocations) {
    return iauctionPathToZoneSlug(iauctionLocations);
  }
  return null;
}

/**
 * Tokenize a Greek text blob into a Set of stems suitable for subset
 * comparison. Drops short noise (single Greek letters from abbreviations
 * like "δ.", "ν.", "π."), strips accents, splits on any non-letter, and
 * greekStem's each token to fold declension variants (Σμύρνης → σμυρν,
 * Σμύρνη → σμυρν).
 */
function tokenizeStems(text: string): Set<string> {
  return new Set(
    stripAccents(text)
      .split(/[^a-zα-ω]+/i)
      .filter((s) => s.length >= 3)
      .map(greekStem),
  );
}

/**
 * Per-zone name-stem index for Greek-text → zone-slug resolution.
 * Built once at module load.
 */
const NAME_EL_INDEX: ReadonlyArray<{
  slug: string;
  level: number;
  region: 'attica' | 'corinth';
  name_el: string;
  name_el_norm: string;
  nameStems: ReadonlySet<string>;
}> = ZONES_BUNDLE.zones.map((z) => ({
  slug: z.slug,
  level: z.level,
  region: z.region,
  name_el: z.name_el,
  name_el_norm: stripAccents(z.name_el),
  nameStems: tokenizeStems(z.name_el),
}));

function countOccurrences(haystack: string, needle: string): number {
  if (!needle || needle.length < 2) return 0;
  let count = 0;
  let i = 0;
  while ((i = haystack.indexOf(needle, i)) !== -1) {
    count++;
    i += needle.length;
  }
  return count;
}

/**
 * Resolve a free-form Greek text blob (typically `rawMunicipality + detail
 * page text` from the eauction.gr scraper) to a canonical Spitogatos zone
 * slug.
 *
 * Match logic:
 *   1. Tokenize + stem the input. Tokenize + stem each zone's name_el.
 *   2. A zone matches if ALL of its name-stems appear in the input.
 *   3. Among matches, score by:
 *      - L2 bonus (100): cron iterates L2 targets, so L2 zones have price
 *        rows. L1 macros and L3 sub-zones don't get crawled, so prefer L2
 *        even if a more specific match exists.
 *      - Substring occurrences × 10: more mentions = stronger primary signal.
 *        (Detail text references many places; the actual auction muni shows
 *        up many more times than incidentals.)
 *      - Stem count: more specific name (more tokens) wins ties.
 *   4. Return highest-scoring slug, or null if no match.
 *
 * Returns null when:
 *   - No zone's stems are a subset of the input (text is unrelated)
 *   - Input is empty / under 3 chars after normalization
 */
export function detectZoneFromText(text: string): string | null {
  if (!text) return null;
  const norm = stripAccents(text);
  const inputStems = tokenizeStems(text);
  if (inputStems.size === 0) return null;

  let best: { slug: string; level: number; score: number } | null = null;
  for (const z of NAME_EL_INDEX) {
    if (z.nameStems.size === 0) continue;
    let allMatch = true;
    for (const s of z.nameStems) {
      if (!inputStems.has(s)) {
        allMatch = false;
        break;
      }
    }
    if (!allMatch) continue;
    const occurrences = Math.max(1, countOccurrences(norm, z.name_el_norm));
    const levelBonus = z.level === 2 ? 100 : z.level === 3 ? 50 : 0;
    const score = levelBonus + occurrences * 10 + z.nameStems.size;
    if (!best || score > best.score) {
      best = { slug: z.slug, level: z.level, score };
    }
  }
  return best?.slug ?? null;
}

/**
 * Walk up the parent chain to find the L2 ancestor of a zone. If the zone
 * IS already L2, returns its own slug. If it's L1 (no L2 ancestor exists),
 * returns null. If it's L3, returns its parent.
 *
 * Used by source modules (iauction, etc.) to bucket coord-resolved zones
 * at the level the price-map cron iterates (L2 = neighborhood). Without
 * this, an L3 hit (like `agios-artemios-pagkrati`) wouldn't match the
 * cron's L2 target (`pagkrati`) and the listing's price contribution
 * would be silently lost.
 */
export function zoneToL2(slug: string): string | null {
  const idx = ZONES_BUNDLE.by_slug[slug];
  if (idx == null) return null;
  const z = ZONES_BUNDLE.zones[idx];
  if (!z) return null;
  if (z.level === 2) return z.slug;
  if (z.level === 1) return null;
  // L3: parent should be L2 by construction; if not, walk up.
  if (!z.parent_slug) return null;
  return zoneToL2(z.parent_slug);
}

/** Public alias for Greek token-stemming (used by the municipality gate). */
export function greekTokens(text: string): Set<string> {
  return tokenizeStems(text);
}

/** Nearest real-polygon L3 by bbox-center distance (bundle centroids are often
 *  null; ×111 ≈ km/degree at Attica's latitude). */
export function nearestRealL3(lat: number, lng: number): { zone: ZoneEntry; km: number } | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  let best: ZoneEntry | null = null;
  let bestD = Infinity;
  for (const z of ZONES_BUNDLE.zones) {
    if (z.level !== 3 || !REAL_POLY_SOURCES.has(z.polygon_source) || !z.bbox) continue;
    const [minLng, minLat, maxLng, maxLat] = z.bbox;
    const d = Math.hypot((minLng + maxLng) / 2 - lng, (minLat + maxLat) / 2 - lat);
    if (d < bestD) { bestD = d; best = z; }
  }
  return best ? { zone: best, km: bestD * 111 } : null;
}

export type ZoneProvenance = 'iauction_coords' | 'appraiser_coords' | 'geocoded' | 'geocoded_nearest' | 'text_match';
export type ZoneResolution = {
  zone_slug: string | null;
  zone_l2_slug: string | null;
  provenance: ZoneProvenance;
  confidence: number;
  nearest_km?: number;
};
export type ResolveInput =
  | { kind: 'coords'; lat: number; lng: number; source: 'iauction' | 'appraiser' | 'geocode'; locationType?: string }
  | { kind: 'text'; text: string };

const HIGH_LOC_TYPES = new Set(['ROOFTOP', 'RANGE_INTERPOLATED']);
const levelOf = (slug: string): number | null => {
  const i = ZONES_BUNDLE.by_slug[slug];
  return i == null ? null : ZONES_BUNDLE.zones[i]?.level ?? null;
};

/** Resolve a lead to its zone with provenance + confidence. Coords inside a real
 *  L3 → that L3 (+L2 parent); coords in a gap → nearest real L3 (always-nearest);
 *  text → detectZoneFromText (L2-biased, low conf). */
export function resolveZone(input: ResolveInput): ZoneResolution {
  if (input.kind === 'text') {
    const slug = detectZoneFromText(input.text);
    return { zone_slug: slug, zone_l2_slug: slug ? zoneToL2(slug) : null, provenance: 'text_match', confidence: 0.4 };
  }
  const { lat, lng, source, locationType } = input;
  const trusted = source === 'iauction' || source === 'appraiser';
  const coordProvenance: ZoneProvenance = source === 'iauction' ? 'iauction_coords' : source === 'appraiser' ? 'appraiser_coords' : 'geocoded';
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { zone_slug: null, zone_l2_slug: null, provenance: source === 'geocode' ? 'geocoded' : coordProvenance, confidence: 0 };
  }
  const direct = coordsToZoneSlug(lat, lng);
  if (direct != null && levelOf(direct) === 3) {
    const high = trusted || (locationType != null && HIGH_LOC_TYPES.has(locationType));
    return { zone_slug: direct, zone_l2_slug: zoneToL2(direct), provenance: coordProvenance, confidence: high ? 0.9 : 0.6 };
  }
  const near = nearestRealL3(lat, lng);
  if (near) {
    return { zone_slug: near.zone.slug, zone_l2_slug: zoneToL2(near.zone.slug), provenance: trusted ? coordProvenance : 'geocoded_nearest', confidence: trusted ? 0.7 : 0.55, nearest_km: Math.round(near.km * 100) / 100 };
  }
  return { zone_slug: direct, zone_l2_slug: direct ? zoneToL2(direct) : null, provenance: trusted ? coordProvenance : 'geocoded_nearest', confidence: 0.5 };
}

export type { ZoneEntry, ZonesBundle } from './zones-bundle.types';
